/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';

import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import LoadingSpinner from '../LoadingSpinner';

const styles = cssMap({
	loadingContainer: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '100vh',
		WebkitFontSmoothing: 'antialiased',
		MozOsxFontSmoothing: 'grayscale',
	},
	nonMobileLoadingContainer: {
		display: 'unset',
		'@media (max-width: 30rem)': {
			display: 'none',
		},
	},
	mobileLoadingContainer: {
		display: 'none',
		'@media (max-width: 30rem)': {
			display: 'unset',
		},
	},
	spinnerContainer: {
		display: 'contents',
	},
});

export type LoadingViewProps = {
	/**
	 * Optional UFO load-hold name. When provided, this view registers a
	 * hold for its lifetime so the surrounding UFO interaction stays open
	 * until the loading state resolves.
	 */
	holdName?: string;
	/**
	 * The logged-out landing page uses a dark shell so the hero can take over
	 * without a visible spinner flash once the lazy chunk resolves.
	 */
	appearance?: 'spinner' | 'shell';
};

/**
 * NOTE: This component can NEVER have a FG. It is used within
 * UserProvider to block descendants from mounting until `fetchUserProfile` (and the
 * awaited `onUserLoginComplete`, which bootstraps Statsig feature
 * gates) has resolved.
 */
export const LoadingView = ({
	holdName,
	appearance = 'spinner',
}: LoadingViewProps = {}): JSX.Element => {
	const view = (
		<div css={styles.loadingContainer}>
			{appearance === 'spinner' && (
				<div css={styles.spinnerContainer}>
					<div css={styles.mobileLoadingContainer}>
						<LoadingSpinner size="large" />
					</div>
					<div css={styles.nonMobileLoadingContainer}>
						<LoadingSpinner size="xlarge" />
					</div>
				</div>
			)}
		</div>
	);

	if (!holdName) {
		return view;
	}

	return <UFOLoadHold name={holdName}>{view}</UFOLoadHold>;
};
